export type GraphData = {
  total: number;
  values: number[];
};

export type CoachProfile = {
  name: string;
  images: string[];
  aboutMe: string;
  specialties: string[];
  customQuestions: string[];
};
export type PlanPhase = {
  description: string;
  id: string;
  name: string;
  numberOfPhase: number;
  planId: string;
  weeks: PlanWeek[];
};

export type PlanTemplatePhase = {
  id: string;
  planTemplateId: string;
  name: string;
  description: string;
  numberOfPhase: number;
  weekTemplates: PlanTemplateWeek[];
};

export type Exercise = {
  avgPace: number | null;
  completedDistance: number | null;
  completedReps: number | null;
  completedSets: number | null;
  completedTime: number | null;
  completedWeight: number | null;
  date: string;
  description: string;
  distance: number | null;
  exerciseTemplateId: string;
  hasResults: boolean;
  id: string;
  isLiked: boolean;
  isMarkedAsKey: boolean;
  name: string;
  numberOfExercise: number;
  pace: number | null;
  paceId: string | null;
  reps: string;
  sets: string;
  time: number | null;
  units: string | null;
  weight: string;
  workoutId: string;
  workoutStatusId: WorkoutCategory;
  notes: string | null;
};

export type WorkoutStatus =
  | "no-results"
  | "partially-done"
  | "did-it"
  | "did-not-do-it";

export enum WorkoutStatusEnum {
  noResults = "no-results",
  partiallyDone = "partially-done",
  didIt = "did-it",
  didNotDoIt = "did-not-do-it",
}

export enum WorkoutCategories {
  RUN = 1,
  SWIM = 2,
  FITNESS = 3,
}

export enum RunWorkoutTypes {
  EASY = 1,
  LONG = 2,
  SPEED = 3,
  CONDITIONING = 4,
  OTHER = 5,
}

export enum SwimWorkoutTypes {
  EASY = 6,
  ENDURANCE = 7,
  SPEED = 8,
  CONDITIONING = 9,
  OTHER = 10,
}

export enum FitnessWorkoutTypes {
  CALISTHENICS = 11,
  WEIGHT_TRAINING = 12,
  CARDIO = 13,
  STRETCH = 14,
  OTHER = 15,
}

export const WorkoutCategoriesList = [
  {
    id: WorkoutCategories.RUN,
    name: "Run",
  },
  {
    id: WorkoutCategories.SWIM,
    name: "Swim",
  },
  {
    id: WorkoutCategories.FITNESS,
    name: "Fitness",
  },
];

export const RunWorkoutTypesList = [
  {
    id: RunWorkoutTypes.EASY,
    name: "Easy",
  },
  {
    id: RunWorkoutTypes.LONG,
    name: "Long",
  },
  {
    id: RunWorkoutTypes.SPEED,
    name: "Speed",
  },
  {
    id: RunWorkoutTypes.CONDITIONING,
    name: "Conditioning",
  },
  {
    id: RunWorkoutTypes.OTHER,
    name: "Other",
  },
];

export const SwimWorkoutTypesList = [
  {
    id: SwimWorkoutTypes.EASY,
    name: "Easy",
  },
  {
    id: SwimWorkoutTypes.ENDURANCE,
    name: "Endurance",
  },
  {
    id: SwimWorkoutTypes.SPEED,
    name: "Speed",
  },
  {
    id: SwimWorkoutTypes.CONDITIONING,
    name: "Conditioning",
  },
  {
    id: SwimWorkoutTypes.OTHER,
    name: "Other",
  },
];

export const FitnessWorkoutTypesList = [
  {
    id: FitnessWorkoutTypes.CALISTHENICS,
    name: "Calisthenics",
  },
  {
    id: FitnessWorkoutTypes.WEIGHT_TRAINING,
    name: "Weight Training",
  },
  {
    id: FitnessWorkoutTypes.CARDIO,
    name: "Cardio",
  },
  {
    id: FitnessWorkoutTypes.STRETCH,
    name: "Stretch",
  },
  {
    id: FitnessWorkoutTypes.OTHER,
    name: "Other",
  },
];

export type WorkoutCategory = {
  id: WorkoutCategories;
  name: string;
};

export type WorkoutType = {
  id: number;
  name: string;
  requiredFields: string[];
  categoryId: number;
};

export type PlanWorkout = {
  averagePace: string | number | null;
  id: string;
  weekId: string;
  name: string;
  description: string;
  dayNumber: number;
  date: string;
  scheduledMessage: string | null;
  estimatedTime: string | null;
  distance: number | null;
  workoutStatusId: WorkoutStatus;
  completedDistance: number | null;
  completedTime: string | null;
  workoutCategoryId: WorkoutCategories;
  workoutTypeId: number;
  workoutCategory: WorkoutCategory;
  workoutType: WorkoutType;
  exercises: Exercise[];
};

export type PlanWeek = {
  description: string;
  /**
   * @format 2025-07-06T00:00:00.999Z
   */
  end: string;
  id: string;
  numberOfWeek: number;
  phaseId: string;
  start: string;
  workouts: PlanWorkout[];
};
export type Plan = {
  id: string;
  name: string;
  coachId?: string;
  isActive: boolean;
  scheduledMessage?: string;
  updatedAt?: string;
  planDescription?: string;
  bannerImageUrls: string[];
  navigate?: () => Promise<unknown>;
  planInfo?: string[];
  phases: PlanPhase[];
  coach?: CoachProfile;
  isPremium?: boolean;
  editWorkoutScheduleNotificationRead?: boolean;
  weekCount?: number;
  iconUrl?: string;
  planTemplateId?: string;
};

export type PlanTemplate = {
  id: string;
  name: string;
  coachId: string;
  isActive: boolean;
  scheduledMessage: string | null;
  updatedAt: string;
  createdAt: string;
  bannerImageUrls: string[];
  planDescription: string;
  planInfo: string[];
  planType: string;
  organizationId: string;
  iconUrl: string | null;
  phaseTemplates: PlanTemplatePhase[];
};

export type PlanTemplateWeek = {
  id: string;
  phaseTemplateId: string;
  numberOfWeek: number;
  description: string;
  workoutTemplates: PlanTemplateWorkout[];
};

export type PlanTemplateWorkout = {
  id: string;
  name: string;
  description: string;
  distance: number | null;
  estimatedTime: string | null;
  workoutCategoryId: WorkoutCategories;
  workoutTypeId: number;
  workoutLibraryExercises: Exercise[];
};

export type PlanMeta = {
  phases: {
    current: number;
    total: number;
  };
  weeks: {
    current: number;
    total: number;
  };
};

export type Message = {
  id: string;
  planId: string | null;
  workoutId: string | null;
  workoutName: string;
  workout: string | null;
  exerciseId: string | null;
  senderId: string;
  senderName: string;
  senderAvatarUrl: string | null;
  receiverId: string;
  receiverName: string;
  receiverAvatarUrl: string | null;
  date: string;
  message: string;
  readDate: string;
};

export type Chat = {
  userId: string;
  name: string;
  avatarUrl: string | null;
  lastMessage: string;
  lastMessageDate: string;
  unreadCount: number;
};

export type GroupChat = {
  id: string;
  name: string;
  ownerId: string;
  organizationId: string;
  metadata: {
    coachName: string;
    organizationName: string;
  };
  imageUrl: string | null;
  createdAt: string;
  deletedAt: string | null;
  member: boolean;
};